import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { LayoutDashboard, ClipboardList, BookOpen, LogOut, Menu, X, GraduationCap } from "lucide-react";
import { Logo } from "../components/ui/Logo";

const navItems = [
  { path: "/aula/dashboard", icon: LayoutDashboard, label: "Inicio" },
  { path: "/aula/practicas", icon: ClipboardList, label: "Mis prácticas" },
  { path: "/aula/casos", icon: BookOpen, label: "Casos clínicos" },
];

function navIsActive(pathname, itemPath) {
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

export default function AulaLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const displayName = user?.nombre || user?.email || "Estudiante";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50/40 to-slate-50">
      <header className="fixed top-0 left-0 right-0 z-40 flex h-16 items-center justify-between border-b border-violet-200/80 bg-white/95 px-3 backdrop-blur-sm lg:hidden">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="h-10 w-10 shrink-0 text-violet-700 hover:bg-violet-50"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
        <span className="truncate text-sm font-semibold text-violet-900">Aurora Aula</span>
        <div className="h-10 w-10 shrink-0" aria-hidden />
      </header>

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden
        />
      )}

      <aside
        className={`fixed top-0 left-0 z-40 h-full w-64 border-r border-violet-200/80 bg-white transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        <div className="flex h-16 items-center gap-2 border-b border-violet-100 px-4">
          <Logo variant="icon" size="sm" className="h-8 w-8" alt="" />
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-violet-900">Aurora Aula</p>
            <p className="truncate text-[11px] text-violet-600/80">Formación geriátrica</p>
          </div>
        </div>

        <ScrollArea className="h-[calc(100vh-11rem)]">
          <nav className="space-y-1 p-3">
            {navItems.map((item) => {
              const isActive = navIsActive(location.pathname, item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors
                    ${isActive
                      ? "bg-violet-600 text-white shadow-sm"
                      : "text-slate-700 hover:bg-violet-50 hover:text-violet-900"}`}
                >
                  <item.icon className="h-5 w-5 shrink-0" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        <div className="absolute bottom-0 left-0 right-0 border-t border-violet-100 bg-white p-3">
          <div className="mb-2 flex items-center gap-2 px-1">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-100 text-violet-700">
              <GraduationCap className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-900">{displayName}</p>
              <p className="truncate text-xs text-slate-500">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:bg-red-50 hover:text-red-700"
            onClick={handleLogout}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Cerrar sesión
          </Button>
        </div>
      </aside>

      <div className="flex min-h-screen flex-col lg:ml-64 lg:pt-0 pt-16">
        <header className="hidden shrink-0 items-center justify-between border-b border-violet-100 bg-white/90 px-6 py-4 backdrop-blur-sm lg:flex">
          <div>
            <p className="text-lg font-semibold text-slate-900">{displayName}</p>
            <p className="text-sm text-slate-500">{user?.email}</p>
          </div>
          <span className="inline-flex items-center rounded-full border border-violet-200 bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-800">
            Aurora Aula
          </span>
        </header>

        <main className="flex-1 p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
